import { marketplacePickupSources } from '../../../../constants/kickavenue';

/**
 * Everything the Pre Delivering marketplace pickup modal needs to read off a sale.
 *
 * TikTok and Shopee sales carry their AWB and pickup PIN from marketplace
 * tracking, so Operations only confirms that the parcel left — it never types a
 * courier or an AWB. Every other source keeps the existing courier form, so each
 * resolver here is written to fail closed: an unrecognised sale resolves to no
 * marketplace source and the caller falls through to the unchanged flow.
 */

// Shown wherever a logistics value is absent. The ticket asks for '-' rather
// than an empty cell so the row still reads as "known to be missing".
export const EMPTY_LOGISTICS_DISPLAY = '-';

// The backend upper-cases these ('TIKTOK' / 'SHOPEE') while the CMS matches the
// lowercase values salesCreatedSourceList renders. Compare normalised: an
// unmatched source resolves to '' silently, so a casing mismatch would drop the
// sale back to the courier form with no error to notice.
const normaliseSource = (value) => (
  typeof value === 'string' ? value.trim().toLowerCase() : ''
);

/**
 * Resolves the marketplace channel a sale belongs to, or '' for anything that is
 * not an in-scope marketplace sale.
 *
 * `offer.mp_channel` is preferred because it is the field the marketplace
 * pipelines write, and it is set on both the Anchanto and the direct routes.
 * `created_source` is only a fallback: it is right today, but it also carries
 * values ('marketplace', 'anchanto') that say nothing about the channel.
 */
export const resolveMarketplaceSource = (sale) => {
  if (sale == null) return '';

  const preferred = normaliseSource(sale.offer && sale.offer.mp_channel);
  // Falls back only when mp_channel is genuinely absent — a present-but-other
  // channel must NOT be overridden by created_source.
  const source = preferred || normaliseSource(sale.created_source);

  return marketplacePickupSources.includes(source) ? source : '';
};

/**
 * True when this sale is confirmed through the marketplace pickup endpoint
 * rather than the courier form.
 */
export const isMarketplacePickupSale = (sale) => resolveMarketplaceSource(sale) !== '';

// Both values live on sale_shipping — NOT on `seller_awb_number`, which is the
// seller's own leg of the shipment and a different number entirely.
const BUYER_CANCELLATION_AWB = 'CANCELLED_BY_BUYER';

const readShippingField = (sale, field) => {
  const raw = sale && sale.sale_shipping ? sale.sale_shipping[field] : null;
  return raw == null ? '' : String(raw).trim();
};

const isReceivedAwb = (awb) => awb !== '' && awb !== BUYER_CANCELLATION_AWB;

/**
 * The marketplace's own order number for this sale — TikTok Shop's or Shopee's
 * id, not ours. Operations needs it to line a Pre Delivering row up against the
 * order in the marketplace back office, where our invoice number means nothing.
 *
 * The stamped channel column is preferred; `offer.mp_order_id` is the documented
 * fallback and is the one populated today, so this reads correctly both before
 * and after the flattened marketplace fields land.
 *
 * Returns '' when there is nothing to show, so the caller can skip the row
 * rather than print an empty label.
 */
export const resolveExternalOrderId = (sale) => {
  if (sale == null) return '';

  // Same precedence as resolveMarketplaceOrderId in MarketplaceLabelHelper —
  // stamped channel field first, then the offer's copy, which is the one the
  // Anchanto route populates. Keeping the two rules identical matters: the label
  // and this row describe the same order, and a channel-keyed rule here would
  // disagree with the label whenever mp_channel is absent.
  const stamped = sale.tiktok_order_id || sale.shopee_order_id;
  const fallback = sale.offer ? sale.offer.mp_order_id : null;
  const raw = stamped || fallback;

  return raw == null ? '' : String(raw).trim();
};

/**
 * The single place the modal reads AWB and pickup code.
 *
 * `hasAwb` is kept separate from the display string on purpose: the confirm gate
 * must key off real presence, and '-' is a truthy string that would otherwise
 * enable the button on a sale with no tracking at all.
 */
export const resolveMarketplaceLogistics = (sale) => {
  const awb = readShippingField(sale, 'awb_number');
  const pickupCode = readShippingField(sale, 'pickup_code');
  const hasAwb = isReceivedAwb(awb);

  return {
    awb: hasAwb ? awb : EMPTY_LOGISTICS_DISPLAY,
    pickupCode: pickupCode === '' ? EMPTY_LOGISTICS_DISPLAY : pickupCode,
    hasAwb,
  };
};
